// Thin DS adapters for the auth, org and account pages (reset PR2). They keep the props those
// pages already used (`loading`, `tone="error"`, a link that looks like a button) so the page
// logic stays exactly as it was; only the components underneath changed. The legacy
// components/ui.tsx versions stay for the screens PR3/PR4/PR7 restyle.
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Button, Callout, type ButtonProps } from "@grackle/ui";

/** DS Button that is disabled and aria-busy while `loading`, like the legacy ui.tsx Button. */
export function LoadingButton({ loading, disabled, ...rest }: ButtonProps & { loading?: boolean }) {
  return <Button disabled={disabled || loading} aria-busy={loading} {...rest} />;
}

/** A router link styled as a DS button (keeps link semantics: middle-click, copy link). */
export function ButtonLink({
  to,
  variant = "primary",
  block,
  children,
}: {
  to: string;
  variant?: "primary" | "secondary" | "ghost";
  block?: boolean;
  children: ReactNode;
}) {
  const cls = ["gk-btn", `gk-btn--${variant}`, block && "gk-btn--block"].filter(Boolean).join(" ");
  return (
    <Link to={to} className={cls}>
      {children}
    </Link>
  );
}

/** The legacy Notice's two tones on DS Callout: neutral information, or an error. Status is
 * never colour alone, so each tone carries its icon. */
export function FormNotice({
  tone = "info",
  icon,
  children,
}: {
  tone?: "info" | "error" | "success";
  icon?: string;
  children: ReactNode;
}) {
  if (tone === "error") {
    return (
      <Callout tone="danger" icon={icon ?? "warning-circle"}>
        {children}
      </Callout>
    );
  }
  return (
    <Callout tone={tone} icon={icon ?? (tone === "success" ? "check-circle" : "info")}>
      {children}
    </Callout>
  );
}
